/**
 * SyncLogDetailDialog Component
 * Dialog showing sync log details with summary and filterable location list.
 */

import { useState } from 'react';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useSyncLogSummary, useSyncLogDetails } from '@/api/sync-logs';
import type { SyncLog, SyncLogChangeType } from '@/types/api';
import { LocationChangeCard } from './LocationChangeCard';

interface SyncLogDetailDialogProps {
  syncLog: SyncLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CHANGE_TYPE_OPTIONS: { value: SyncLogChangeType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Changes' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'unchanged', label: 'Unchanged' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'error', label: 'Errors' },
];

export function SyncLogDetailDialog({
  syncLog,
  open,
  onOpenChange,
}: SyncLogDetailDialogProps): JSX.Element {
  const [changeTypeFilter, setChangeTypeFilter] = useState<SyncLogChangeType | 'all'>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const syncLogId = syncLog?.id ?? null;

  const { data: summary, isLoading: summaryLoading } = useSyncLogSummary(syncLogId);
  const { data: detailsResponse, isLoading: detailsLoading } = useSyncLogDetails(
    syncLogId,
    {
      page,
      limit,
      changeType: changeTypeFilter === 'all' ? undefined : changeTypeFilter,
    }
  );

  const details = detailsResponse?.data ?? [];
  const pagination = detailsResponse?.pagination;

  // Reset page when filter changes
  const handleFilterChange = (value: string): void => {
    setChangeTypeFilter(value as SyncLogChangeType | 'all');
    setPage(1);
  };

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        data-testid="sync-log-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>Sync Details</DialogTitle>
        </DialogHeader>

        {/* Sync info header */}
        {syncLog && (
          <div className="text-sm text-muted-foreground border-b pb-3">
            <span>
              {syncLog.syncType === 'initial' ? 'Initial' : 'Incremental'} sync
              {syncLog.startedAt && ` on ${formatDate(syncLog.startedAt)}`}
            </span>
          </div>
        )}

        {/* Summary badges */}
        <div
          className="flex flex-wrap gap-2 py-3 border-b"
          data-testid="sync-log-detail-summary"
        >
          {summaryLoading ? (
            <>
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-24" />
            </>
          ) : summary && summary.total > 0 ? (
            // On total, not hasDetails: since 2026-10-08 an unchanged listing is counted on the
            // sync log, not written as a detail row, so an all-unchanged sync has counts but no rows.
            <>
              {summary.created > 0 && (
                <Badge variant="default" className="bg-green-500 hover:bg-green-500">
                  {String(summary.created)} created
                </Badge>
              )}
              {summary.updated > 0 && (
                <Badge variant="default" className="bg-blue-500 hover:bg-blue-500">
                  {String(summary.updated)} updated
                </Badge>
              )}
              {summary.unchanged > 0 && (
                <Badge variant="secondary" data-testid="sync-log-detail-unchanged">
                  {String(summary.unchanged)} unchanged
                </Badge>
              )}
              {summary.deleted > 0 && (
                <Badge variant="destructive">
                  {String(summary.deleted)} deleted
                </Badge>
              )}
              {summary.error > 0 && (
                <Badge variant="destructive">
                  {String(summary.error)} errors
                </Badge>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Info className="w-4 h-4" />
              <span>Details not available for this sync log</span>
            </div>
          )}
        </div>

        {/* Filter */}
        {summary?.hasDetails && (
          <div className="flex items-center gap-3" data-testid="sync-log-detail-filter">
            <span className="text-sm text-muted-foreground">Filter:</span>
            <Select value={changeTypeFilter} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANGE_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Details list */}
        <div
          className="flex-1 overflow-y-auto space-y-2 min-h-0"
          data-testid="sync-log-detail-table"
        >
          {detailsLoading ? (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          ) : details.length > 0 ? (
            details.map((detail) => (
              <LocationChangeCard key={detail.id} detail={detail} />
            ))
          ) : summary?.hasDetails ? (
            <div className="text-center text-muted-foreground py-8">
              No locations match the current filter
            </div>
          ) : summary && summary.total > 0 ? (
            <div
              className="text-center text-muted-foreground py-8"
              data-testid="sync-log-detail-all-unchanged"
            >
              <p>No listing changed in this sync.</p>
              <p className="text-xs mt-1">Unchanged listings are counted, not listed.</p>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <p>Details not available for this sync log.</p>
              <p className="text-xs mt-1">
                (Sync log details are only recorded for syncs started after this feature was enabled)
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t">
            <span className="text-sm text-muted-foreground">
              Page {String(pagination.page)} of {String(pagination.totalPages)}
              {' '}({String(pagination.total)} total)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setPage(page - 1); }}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setPage(page + 1); }}
                disabled={page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
